import { OrganizationFileResult } from '../../../api/file-upload-api-client';

import { LanguageState, MessageComposerContextState } from './message-composer-state';

import { LanguageCode } from '../../../util/languages';
import { MESSAGE_TYPE, MessageType } from '../../../util/message-types';
import { checkMessageForErrorsAndWarnings } from '../util/check-message-for-errors-and-warnings';
import {
  LONGEST_ALLOWABLE_TEXT,
  LONG_TEXT_MESSAGE_ERROR,
} from '../util/message-composer-constants';

export type MessageContextAction =
  | {
      type: 'RESET_LANGUAGE_STATE';
      state: Record<LanguageCode, LanguageState>;
      forceUpdate: boolean;
    }
  | {
      type: 'UPDATE_MESSAGE_FOR_LANGUAGE';
      languageCode: LanguageCode;
      message: string;
      forceUpdate: boolean;
    }
  | {
      type: 'REPLACE_LANGUAGE_TO_MESSAGE';
      languageToMessage: Record<LanguageCode, string>;
      forceUpdate: boolean;
    }
  | {
      type: 'UPLOAD_FILE_FOR_LANGUAGE';
      languageCode: LanguageCode;
      fileName: string;
      resultLink: OrganizationFileResult;
    }
  | {
      type: 'REMOVE_FILE_FOR_LANGUAGE';
      languageCode: LanguageCode;
      urlFileMapping: UrlFileMapping;
    }
  | {
      type: 'UPDATE_MESSAGE_TYPE';
      messageType: MessageType;
      forceUpdate: boolean;
    }
  | {
      type: 'UPDATE_MESSAGE_ERROR_FOR_LANGUAGE';
      languageCode: LanguageCode;
      messageError: string | null;
    }
  | {
      type: 'UPDATE_SUBJECT_ERROR_FOR_LANGUAGE';
      languageCode: LanguageCode;
      subjectError: string | null;
    }
  | {
      type: 'UPDATE_IS_TRANSLATING';
      isTranslating: boolean;
      languageCode: LanguageCode;
    }
  | {
      type: 'UPDATE_STREAMING_HACK_TEXT';
      streamingHackText: string;
      languageCode: LanguageCode;
    }
  | {
      type: 'STREAMING_HACK_DONE';
      languageCode: LanguageCode;
    };

const checkForLangToStateErrors = (
  languageToState: MessageComposerContextState['languageToState'],
  type: MessageType,
  disableSubject?: boolean,
) => {
  let hasErrors = false;
  Object.keys(languageToState).forEach((lc) => {
    if (
      languageToState[lc].messageError ||
      (type === 'email' && languageToState[lc].subjectError && !disableSubject)
    ) {
      hasErrors = true;
    }
  });
  return hasErrors;
};

const updateStateForLanguage = (
  state: MessageComposerContextState,
  languageCode: LanguageCode,
  update: Partial<LanguageState>,
) => {
  const { languageToState } = state;
  return {
    ...state,
    languageToState: {
      ...languageToState,
      [languageCode]: {
        ...languageToState[languageCode],
        ...update,
      },
    },
  };
};

/**
 * Computes the count of files uploaded for a given languageToState
 * NOTE: includes files that have been removed. See comment in
 * 'UPLOAD_FILE_FOR_LANGUAGE' action for why we count deleted files here
 * @param state languageToState
 * @returns file count
 */
const getFileCount = (state: Record<LanguageCode, LanguageState>) =>
  Object.keys(state).reduce<number>(
    (sum, languageCode) => sum + (state[languageCode]?.urlFileMappings?.length || 0),
    0,
  );

export const messageReducer = (
  state: MessageComposerContextState,
  action: MessageContextAction,
): MessageComposerContextState => {
  switch (action.type) {
    case 'RESET_LANGUAGE_STATE': {
      return {
        ...state,
        languageToState: action.state,
        fileCount: getFileCount(action.state),
        // defaults to true (see action) if not specified as the caller
        // presumably wants to affect this change to context consumers
        // maintaining their own state
        forceUpdate: action.forceUpdate,
      };
    }
    case 'UPDATE_MESSAGE_FOR_LANGUAGE': {
      const { languageCode, message } = action;
      const { languageToState, messageType, disableSubject } = state;

      // This prevents checking the default state for errors after sending
      // Specifically, prevents showing a message required error after using
      // replace language to message to set to empty string
      if (languageToState[languageCode].message === message) {
        return {
          ...state,
          // defaults to false (see action) as the caller may not want to force
          // context consumers to update self-maintained state if they have it
          forceUpdate: action.forceUpdate,
        };
      }

      const { error, warning } = checkMessageForErrorsAndWarnings(messageType, message);

      // TODO[PROD-3485]: This error banner isn't good and this behavior
      // of truncating the string could be improved
      let newMessage = message;
      if (error === LONG_TEXT_MESSAGE_ERROR) {
        newMessage = newMessage.substring(0, LONGEST_ALLOWABLE_TEXT);
      }

      const newState = updateStateForLanguage(state, languageCode, {
        message: newMessage,
        urlFileMappings: languageToState[languageCode].urlFileMappings,
        messageError: error,
        messageWarning: warning,
      });

      return {
        ...newState,
        hasErrors: checkForLangToStateErrors(newState.languageToState, messageType, disableSubject),
        // defaults to false (see action) as the caller may not want to force
        // context consumers to update self-maintained state if they have it
        forceUpdate: action.forceUpdate,
      };
    }
    case 'REPLACE_LANGUAGE_TO_MESSAGE': {
      const { languageToMessage, forceUpdate } = action;
      const { messageType, languageToState, disableSubject } = state;

      const newLanguageToState = Object.keys(languageToMessage).reduce((acc, key) => {
        const message = languageToMessage[key];
        const { error, warning } = checkMessageForErrorsAndWarnings(messageType, message);
        acc[key] = {
          // This action only replaces the message state so we carry over
          // other languageToState properties like subjectError
          ...languageToState[key],
          message,
          messageError: error,
          messageWarning: warning,
        };
        return acc;
      }, {} as MessageComposerContextState['languageToState']);

      return {
        ...state,
        hasErrors: checkForLangToStateErrors(newLanguageToState, messageType, disableSubject),
        languageToState: newLanguageToState,
        // defaults to true (see action) if not specified as the caller
        // presumably wants to affect this change to context consumers
        // maintaining their own state
        forceUpdate,
      };
    }
    case 'UPDATE_MESSAGE_TYPE': {
      const { messageType, forceUpdate } = action;
      const { languageToState, disableSubject } = state;

      const newLangToState: Record<LanguageCode, LanguageState> = {};

      // Make sure all the errors are correct for the message type
      Object.keys(languageToState).forEach((lc) => {
        const { message } = languageToState[lc];
        const { error, warning } = checkMessageForErrorsAndWarnings(messageType, message);
        // subject errors are checked in the MessageInput until subject is in context
        newLangToState[lc] = {
          ...languageToState[lc],
          messageError: error,
          messageWarning: warning,
        };
      });

      return {
        ...state,
        languageToState: newLangToState,
        hasErrors: checkForLangToStateErrors(newLangToState, messageType, disableSubject),
        messageType,
        // defaults to false (see action) as the caller may not want to force
        // context consumers to update self-maintained state if they have it
        forceUpdate,
      };
    }
    case 'UPLOAD_FILE_FOR_LANGUAGE': {
      const { languageCode, fileName, resultLink } = action;
      const { languageToState, fileCount } = state;
      const urlKey = `url${fileCount + 1}`;
      // There are more elegant solutions, but to generate keys that increment, we just use the length of urlFileMappings.
      // When a mapping is deleted by a user, we mark the isDeleted field without removing it in the array itself.
      const newUrlFileMappings = (languageToState[languageCode].urlFileMappings || []).concat({
        urlKey,
        downloadableUrl: resultLink.uploadLink,
        fileName,
        isDeleted: false,
        organizationFileId: resultLink.organizationFileId,
      });

      return {
        ...updateStateForLanguage(state, languageCode, {
          ...languageToState[languageCode],
          urlFileMappings: newUrlFileMappings,
        }),
        fileCount: fileCount + 1,
        // hard-code to false as this update has no bearing on the message
        forceUpdate: false,
      };
    }
    case 'REMOVE_FILE_FOR_LANGUAGE': {
      const { languageCode, urlFileMapping } = action;
      const { languageToState } = state;

      const newUrlFileMappings = (languageToState[languageCode].urlFileMappings || []).map(
        (mapping) => {
          if (mapping.urlKey === urlFileMapping.urlKey) {
            mapping.isDeleted = true;
          }
          return mapping;
        },
      );

      return {
        ...updateStateForLanguage(state, languageCode, {
          ...languageToState[languageCode],
          urlFileMappings: newUrlFileMappings,
        }),
        // hard-code to false as this update has no bearing on the message
        forceUpdate: false,
      };
    }
    case 'UPDATE_MESSAGE_ERROR_FOR_LANGUAGE': {
      const { messageError, languageCode } = action;
      const { messageType, disableSubject } = state;

      const newState = updateStateForLanguage(state, languageCode, {
        messageError,
      });

      return {
        ...newState,
        hasErrors: checkForLangToStateErrors(newState.languageToState, messageType, disableSubject),
        // hard-code to false as this update has no bearing on the message
        forceUpdate: false,
      };
    }
    case 'UPDATE_SUBJECT_ERROR_FOR_LANGUAGE': {
      const { subjectError, languageCode } = action;
      const { messageType, disableSubject } = state;

      const newState = updateStateForLanguage(state, languageCode, {
        subjectError,
      });

      return {
        ...newState,
        hasErrors: checkForLangToStateErrors(newState.languageToState, messageType, disableSubject),
        // hard-code to false as this update has no bearing on the message
        forceUpdate: false,
      };
    }
    case 'UPDATE_IS_TRANSLATING': {
      const { isTranslating, languageCode } = action;

      // TODO[PROD-3389]: Individual language loading states
      return {
        ...updateStateForLanguage({ ...state, isLoading: isTranslating }, languageCode, {
          isTranslating,
        }),
        // hard-code to false as this update has no bearing on the message
        forceUpdate: false,
      };
    }
    case 'UPDATE_STREAMING_HACK_TEXT': {
      const { streamingHackText, languageCode } = action;

      return {
        ...updateStateForLanguage(state, languageCode, {
          streamingHackText,
        }),
        // hard-code to false as this update has no bearing on the message
        forceUpdate: false,
      };
    }
    case 'STREAMING_HACK_DONE': {
      const { languageCode } = action;
      const { languageToState, messageType } = state;

      const { streamingHackText, message } = languageToState[languageCode];

      // We need to explicitly convert newlines in the response to HTML line
      // breaks when the rich text editor is being used
      let newMessage = streamingHackText;
      if (newMessage && messageType === MESSAGE_TYPE.EMAIL) {
        newMessage = newMessage.replaceAll('\n', '<br />');
      }

      // TODO[PROD-3389]: Individual language loading states
      const newState = {
        ...updateStateForLanguage({ ...state, isLoading: false }, languageCode, {
          message: newMessage || message,
          streamingHackText: '',
          isTranslating: false,
        }),
        // hard-code to true as this update signals the consumer should
        // overwrite self-maintained state with the now-translated message from
        // context
        forceUpdate: true,
      };
      return newState;
    }
    default:
      return { ...state, forceUpdate: false };
  }
};
